import { Suspense } from "react"
import { lazyWithRetry } from "@/utils/lazyWithRetry"
import { createHashRouter } from "react-router-dom"

import { ErrorBoundary } from "@/components/ErrorBoundary"
import { MobileRedirect } from "@/components/MobileRedirect/MobileRedirect"
import {
    DisclaimerInner,
    DisclaimerPage,
    EULAInner,
    EULAPage,
    PrivacyPolicyInner,
    PrivacyPolicyPage,
    TermsOfUseInner,
    TermsOfUsePage,
} from "@/pages/terms-and-policy/Terms"

const isProduction = import.meta.env.VITE_ENVIRONMENT_NAME === "production"

const Layout = lazyWithRetry(() => import("@/template/layout"))
const DashboardPage = lazyWithRetry(() => import("@/pages/dashboard"))
const ChatPage = lazyWithRetry(() => import("@/pages/chat"))
const WorkflowsPage = lazyWithRetry(() => import("@/pages/workflows"))
const WorkflowDetailsPage = lazyWithRetry(() => import("@/pages/workflowDetails"))
const DocumentsPage = lazyWithRetry(() => import("@/pages/documents"))
const ConversationsPage = lazyWithRetry(() => import("@/pages/conversations"))
const SettingsPage = lazyWithRetry(() => import("@/pages/settings"))
const NoPage = lazyWithRetry(() => import("@/pages/noPage"))
const MobileOopsPage = lazyWithRetry(() => import("@/pages/mobileOops"))
const DeploymentPage = lazyWithRetry(() => import("@/pages/deployment"))
const ConnectorsSetupPage = lazyWithRetry(() => import("@/pages/connectors"))
const HelpAndResources = lazyWithRetry(() => import("@/pages/helpAndResources"))
const NewsPage = lazyWithRetry(() => import("@/pages/news"))
const ProjectsPage = lazyWithRetry(() => import("@/pages/projects"))
const ProjectCreatePage = lazyWithRetry(() => import("@/pages/projects/ProjectCreate"))
const ProjectDetailsPage = lazyWithRetry(() => import("@/pages/projectDetails"))
const AdminPage = lazyWithRetry(() => import("@/pages/admin"))
const EntitiesPage = lazyWithRetry(() => import("@/pages/entities").then(m => ({ default: m.EntitiesPage })))
const DebugWelcomeModal = isProduction
    ? lazyWithRetry(() => import("@/pages/noPage"))
    : lazyWithRetry(() => import("@/test/DebugWelcomeModal"))

const DebugXeokit = isProduction
    ? lazyWithRetry(() => import("@/pages/noPage"))
    : lazyWithRetry(() => import("@/test/DebugXeokit"))

export enum RoutesPath {
    Root = "/",
    Dashboard = "/dashboard",
    Chat = "/chat",
    Workflows = "/workflows",
    WorkflowsDetails = "/workflow-details",
    WorkflowsDetailsWithId = "/workflow-details/:id",
    ChatId = "/chat/:id",
    Upload = "/upload",
    Documents = "/documents",
    DocumentDetails = "/documents/:id?/:name?",
    Conversations = "/conversations",
    Settings = "/settings",
    User = "/user",
    Connectors = "/connectors",
    TermsOfUse = "/terms-of-service",
    TermsOfService = "/terms-of-use",
    PrivacyPolicy = "/privacy-policy",
    Disclaimer = "/disclaimer",
    EULA = "/eula",
    PrivacyPolicyInner = "/privacy-policy-inner",
    EULAInner = "/eula-inner",
    DisclaimerInner = "/disclaimer-inner",
    TermsOfUseInner = "/terms-of-use-inner",
    HelpAndResources = "/help-and-resources",
    News = "/announcements",
    Projects = "/projects",
    Matrix = "/matrix",
    MatrixDetails = "/matrix/:id",
    ProjectCreate = "/projects/new",
    ProjectDetails = "/projects/:id",
    Admin = "/admin",
    Entities = "/entities",
    DebugModal = "/debug-modal",
    DebugXeokit = "/debug-xeokit",
    Deployment = "/deployment",
    NoPage = "*",
}

const router = createHashRouter([
    {
        element: (
            <ErrorBoundary>
                <Layout />
                <MobileRedirect />
            </ErrorBoundary>
        ),
        path: RoutesPath.Root,
        children: [
            {
                index: true,
                element: <ChatPage />,
            },
            {
                path: RoutesPath.Dashboard,
                element: <DashboardPage />,
            },
            {
                path: RoutesPath.Chat,
                element: <ChatPage />,
                children: [
                    {
                        path: RoutesPath.ChatId,
                        element: <ChatPage />,
                    },
                ],
            },
            {
                path: RoutesPath.Workflows,
                element: <WorkflowsPage />,
            },
            {
                path: RoutesPath.WorkflowsDetails,
                element: <WorkflowDetailsPage />,
            },
            {
                path: RoutesPath.WorkflowsDetailsWithId,
                element: <WorkflowDetailsPage />,
            },
            {
                path: RoutesPath.Documents,
                element: <DocumentsPage />,
            },
            {
                path: RoutesPath.DocumentDetails,
                element: <DocumentsPage />,
            },
            {
                path: RoutesPath.Conversations,
                element: <ConversationsPage />,
            },
            {
                path: RoutesPath.Settings,
                element: <SettingsPage />,
            },
            {
                path: RoutesPath.NoPage,
                element: <NoPage />,
            },
            {
                path: RoutesPath.Connectors,
                element: <ConnectorsSetupPage />,
            },
            {
                path: RoutesPath.PrivacyPolicyInner,
                element: <PrivacyPolicyInner />,
            },
            {
                path: RoutesPath.EULAInner,
                element: <EULAInner />,
            },
            {
                path: RoutesPath.DisclaimerInner,
                element: <DisclaimerInner />,
            },
            {
                path: RoutesPath.TermsOfUseInner,
                element: <TermsOfUseInner />,
            },
            {
                path: RoutesPath.HelpAndResources,
                element: <HelpAndResources />,
            },
            {
                path: RoutesPath.News,
                element: <NewsPage />,
            },
            {
                path: RoutesPath.Projects,
                element: <ProjectsPage />,
            },
            {
                path: RoutesPath.ProjectCreate,
                element: <ProjectCreatePage />,
            },
            {
                path: RoutesPath.ProjectDetails,
                element: <ProjectDetailsPage />,
            },
            {
                path: RoutesPath.Admin,
                element: <AdminPage />,
            },
            {
                path: RoutesPath.Entities,
                element: <EntitiesPage />,
            },
            {
                path: RoutesPath.DebugModal,
                element: <DebugWelcomeModal />,
            },
            {
                path: RoutesPath.DebugXeokit,
                element: <DebugXeokit />,
            },
        ],
    },
    {
        path: "/mobile-oops",
        element: <MobileOopsPage />,
    },
    {
        path: RoutesPath.Deployment,
        element: (
            <Suspense fallback={<div style={{ minHeight: "100vh" }} />}>
                <DeploymentPage />
            </Suspense>
        ),
    },
    {
        path: RoutesPath.TermsOfUse,
        element: <TermsOfUsePage />,
    },
    {
        path: RoutesPath.TermsOfService,
        element: <TermsOfUsePage />,
    },
    {
        path: RoutesPath.PrivacyPolicy,
        element: <PrivacyPolicyPage />,
    },
    {
        path: RoutesPath.Disclaimer,
        element: <DisclaimerPage />,
    },
    {
        path: RoutesPath.EULA,
        element: <EULAPage />,
    },
])

export default router
